import * as React from "react";
import { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogActions,
  DialogContent,
  Button,
  Text,
  Caption1,
  Spinner,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import {
  Dismiss24Regular,
  DocumentPdf20Regular,
  ArrowUpload20Regular,
  ArrowDownload20Regular,
} from "@fluentui/react-icons";
import { StatusItemDto } from "../../../services/status-items.service";
import {
  ClientDocumentFileDto,
  getClientDocumentVersions,
  downloadClientDocumentVersion,
  uploadClientDocumentVersion,
} from "../../../services/client-documents.service";
import { useTypographyStyles } from "../../../styles/typography.styles";

const useStyles = makeStyles({
  dialogSurface: {
    maxWidth: "600px",
    minWidth: "450px",
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    marginBottom: tokens.spacingVerticalM,
  },
  headerIcon: {
    color: "#D83B01",
  },
  versionList: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
    maxHeight: "300px",
    overflowY: "auto",
  },
  versionItem: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: tokens.spacingVerticalM,
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  versionInfo: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXS,
    flex: 1,
    minWidth: 0,
  },
  versionHeader: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
  },
  versionMeta: {
    display: "flex",
    gap: tokens.spacingHorizontalM,
  },
  latestBadge: {
    backgroundColor: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundOnBrand,
    padding: `${tokens.spacingVerticalXXS} ${tokens.spacingHorizontalS}`,
    borderRadius: tokens.borderRadiusSmall,
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
  },
  uploadSection: {
    marginTop: tokens.spacingVerticalL,
    padding: tokens.spacingVerticalM,
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: tokens.borderRadiusMedium,
    border: `2px dashed ${tokens.colorNeutralStroke1}`,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: tokens.spacingVerticalS,
    cursor: "pointer",
    transition: "all 0.2s ease",
    "&:hover": {
      border: `2px dashed ${tokens.colorBrandBackground}`,
      backgroundColor: tokens.colorNeutralBackground2,
    },
  },
  uploadSectionDisabled: {
    cursor: "not-allowed",
    opacity: 0.6,
    "&:hover": {
      border: `2px dashed ${tokens.colorNeutralStroke1}`,
      backgroundColor: tokens.colorNeutralBackground3,
    },
  },
  placeholderText: {
    textAlign: "center",
  },
  emptyState: {
    padding: tokens.spacingVerticalXL,
    textAlign: "center",
    color: tokens.colorNeutralForeground3,
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: tokens.spacingVerticalXL,
  },
  errorMessage: {
    color: tokens.colorPaletteRedForeground1,
    padding: tokens.spacingVerticalM,
    backgroundColor: tokens.colorPaletteRedBackground1,
    borderRadius: tokens.borderRadiusMedium,
    marginBottom: tokens.spacingVerticalM,
    textAlign: "center",
  },
  noDocumentMessage: {
    padding: tokens.spacingVerticalL,
    textAlign: "center",
    color: tokens.colorNeutralForeground3,
  },
  hiddenInput: {
    display: "none",
  },
});

interface DocumentVersionHistoryDialogProps {
  isOpen: boolean;
  onDismiss: () => void;
  statusItem: StatusItemDto | null;
  onVersionUploaded?: () => void;
}

/**
 * Modal showing the upload history of a client document — each version with
 * uploader, timestamp, and a download action. Supports uploading a new version.
 */
const DocumentVersionHistoryDialog: React.FC<DocumentVersionHistoryDialogProps> = ({
  isOpen,
  onDismiss,
  statusItem,
  onVersionUploaded,
}) => {
  const styles = useStyles();
  const typography = useTypographyStyles();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // State
  const [versions, setVersions] = useState<ClientDocumentFileDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDownloading, setIsDownloading] = useState<number | null>(null);

  // Fetch versions when dialog opens
  useEffect(() => {
    if (isOpen && statusItem?.documentId) {
      fetchVersions();
    } else if (isOpen && !statusItem?.documentId) {
      // No document linked yet
      setVersions([]);
      setError(null);
    }
  }, [isOpen, statusItem?.documentId]);

  const fetchVersions = async () => {
    if (!statusItem?.documentId) return;

    setIsLoading(true);
    setError(null);

    try {
      const data = await getClientDocumentVersions(statusItem.documentId);
      setVersions(data);
    } catch (err) {
      console.error("Failed to fetch document versions:", err);
      setError("Failed to load version history. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  const handleDownload = async (version: ClientDocumentFileDto) => {
    if (!statusItem?.documentId) return;

    setIsDownloading(version.version);
    try {
      await downloadClientDocumentVersion(
        statusItem.documentId,
        version.version,
        version.fileName
      );
    } catch (err) {
      console.error("Failed to download file:", err);
      setError("Failed to download file. Please try again.");
    } finally {
      setIsDownloading(null);
    }
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !statusItem?.documentId) return;

    // Validate file type
    if (file.type !== "application/pdf") {
      setError("Only PDF files are supported");
      return;
    }

    // Validate file size (50MB)
    if (file.size > 50 * 1024 * 1024) {
      setError("File size must be less than 50MB");
      return;
    }

    setIsUploading(true);
    setError(null);

    try {
      await uploadClientDocumentVersion(statusItem.documentId, file);
      // Refresh the version list
      await fetchVersions();
      // Notify parent if callback provided
      onVersionUploaded?.();
    } catch (err) {
      console.error("Failed to upload new version:", err);
      setError("Failed to upload file. Please try again.");
    } finally {
      setIsUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const openFilePicker = () => {
    if (!isUploading && statusItem?.documentId) {
      fileInputRef.current?.click();
    }
  };

  const hasDocument = !!statusItem?.documentId;

  return (
    <Dialog open={isOpen} onOpenChange={(_e, data) => !data.open && onDismiss()}>
      <DialogSurface className={styles.dialogSurface}>
        <DialogBody>
          <DialogTitle
            action={
              <Button
                appearance="subtle"
                aria-label="close"
                icon={<Dismiss24Regular />}
                onClick={onDismiss}
              />
            }
          >
            Version History
          </DialogTitle>

          <DialogContent>
            {/* Document Header */}
            <div className={styles.header}>
              <DocumentPdf20Regular className={styles.headerIcon} />
              <Text className={typography.formLabel}>{statusItem?.name || "Document"}</Text>
            </div>

            {/* Error Message */}
            {error && (
              <div className={styles.errorMessage}>
                <Text>{error}</Text>
              </div>
            )}

            {/* No Document Linked */}
            {!hasDocument && (
              <div className={styles.noDocumentMessage}>
                <Text>No document has been uploaded for this status item yet.</Text>
              </div>
            )}

            {/* Loading State */}
            {hasDocument && isLoading && (
              <div className={styles.loadingContainer}>
                <Spinner size="medium" label="Loading versions..." />
              </div>
            )}

            {/* Version List */}
            {hasDocument && !isLoading && versions.length > 0 && (
              <div className={styles.versionList}>
                {versions.map((version, index) => (
                  <div key={version.id} className={styles.versionItem}>
                    <div className={styles.versionInfo}>
                      <div className={styles.versionHeader}>
                        <Text className={typography.formLabel}>Version {version.version}</Text>
                        {index === 0 && (
                          <span className={styles.latestBadge}>Latest</span>
                        )}
                      </div>
                      <Caption1>{version.fileName}</Caption1>
                      <div className={styles.versionMeta}>
                        <Caption1>{formatFileSize(version.fileSize)}</Caption1>
                        <Caption1>Uploaded {formatDate(version.uploadedAt)}</Caption1>
                      </div>
                      {version.uploadedByName && (
                        <Caption1>by {version.uploadedByName}</Caption1>
                      )}
                    </div>
                    <Button
                      appearance="subtle"
                      icon={<ArrowDownload20Regular />}
                      onClick={() => handleDownload(version)}
                      disabled={isDownloading === version.version}
                      aria-label={`Download version ${version.version}`}
                    >
                      {isDownloading === version.version ? "..." : ""}
                    </Button>
                  </div>
                ))}
              </div>
            )}

            {/* Empty State */}
            {hasDocument && !isLoading && versions.length === 0 && !error && (
              <div className={styles.emptyState}>
                <Text>No versions found</Text>
              </div>
            )}

            {/* Upload New Version Section */}
            {hasDocument && (
              <>
                {/* Hidden file input */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="application/pdf"
                  onChange={handleFileSelect}
                  className={styles.hiddenInput}
                  disabled={isUploading}
                  aria-label="Select PDF document for new version"
                />

                <div
                  className={`${styles.uploadSection} ${isUploading ? styles.uploadSectionDisabled : ""}`}
                  onClick={openFilePicker}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if ((e.key === "Enter" || e.key === " ") && !isUploading) {
                      openFilePicker();
                    }
                  }}
                >
                  {isUploading ? (
                    <Spinner size="small" label="Uploading..." />
                  ) : (
                    <>
                      <ArrowUpload20Regular style={{ color: tokens.colorNeutralForeground3 }} />
                      <Text className={`${typography.caption} ${styles.placeholderText}`}>
                        Click to upload a new version
                      </Text>
                      {/* <Button
                        appearance="secondary"
                        icon={<ArrowUpload20Regular />}
                        onClick={(e) => {
                          e.stopPropagation();
                          openFilePicker();
                        }}
                        disabled={isUploading}
                      >
                        Upload New Version
                      </Button> */}
                    </>
                  )}
                </div>
              </>
            )}
          </DialogContent>

          <DialogActions>
            <Button appearance="secondary" onClick={onDismiss}>
              Close
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default DocumentVersionHistoryDialog;
